/**
 * Backs the `character varying(255)` counterparty contact-name columns, so the
 * request-validation bound and the persisted bound stay the same number.
 */
export const MAX_COUNTERPARTY_CONTACT_FIELD_LENGTH = 255;

/**
 * The note is stored as unbounded `text`, so this is purely a request-validation
 * bound that keeps a single free-text field from blowing up payloads.
 */
export const MAX_COUNTERPARTY_NOTE_LENGTH = 5000;

/**
 * Upper bound on how many contacts a single counterparty may hold. Enforced
 * under the per-counterparty details lock so concurrent creates cannot exceed
 * it. Slice-1 cap; revisit if directory users legitimately need more.
 */
export const MAX_COUNTERPARTY_CONTACTS_PER_COUNTERPARTY = 50;

/**
 * Upper bound on how many addresses a single counterparty may hold. See
 * {@link MAX_COUNTERPARTY_CONTACTS_PER_COUNTERPARTY}.
 */
export const MAX_COUNTERPARTY_ADDRESSES_PER_COUNTERPARTY = 50;

export enum CounterpartiesReturnFormat {
    JSON = "json",
    CSV = "csv",
}

export enum DirectorySort {
    NAME_ASC = "name_asc",
    NAME_DESC = "name_desc",
    SOURCE_ASC = "source_asc",
    SOURCE_DESC = "source_desc",
    JOURNAL_ENTRY_LINES_ASC = "journal_entry_lines_asc",
    JOURNAL_ENTRY_LINES_DESC = "journal_entry_lines_desc",
    DEBIT_ASC = "debit_asc",
    DEBIT_DESC = "debit_desc",
    CREDIT_ASC = "credit_asc",
    CREDIT_DESC = "credit_desc",
    SPEND_DESC = "spend_desc",
    REVENUE_DESC = "revenue_desc",
    ACCOUNTS_COUNT_ASC = "accounts_count_asc",
    ACCOUNTS_COUNT_DESC = "accounts_count_desc",
}

export enum CounterpartyVerifiedScope {
    LOCAL = "local",
    GLOBAL = "global",
}

/**
 * Directory page filter for counterparty disabled state. Absent or ALL means
 * no filtering, so disabled counterparties stay visible on the Counterparties
 * page by default.
 */
export enum CounterpartyDisabledFilter {
    ALL = "all",
    ONLY_ENABLED = "only_enabled",
    ONLY_DISABLED = "only_disabled",
}

/**
 * Level at which the counterparty directory amount filter is applied: against
 * each counterparty's aggregated totals, or against individual journal entry
 * amounts.
 */
export enum AmountFilterLevel {
    COUNTERPARTY = "counterparty",
    JOURNAL_ENTRY = "journal_entry",
}

/**
 * Transaction update sources that represent user-initiated counterparty assignments.
 * Used to protect user-assigned counterparties from being overwritten by automated processes.
 */
export { USER_ASSIGNED_COUNTERPARTY_SOURCES } from "./counterparty-assignment";
export {
    isUserAssignedCounterpartySource,
    isAutomatedCounterpartyAssignmentSource,
} from "./counterparty-assignment";
